import React, { useState } from 'react'
import { useTranslation } from '@structura/i18n'

interface SampleReportFile {
  materialType: string
  fileName: string
  fullPath: string
  fileSizeBytes: number
}

export interface SamplesSectionProps {
  getSessionHeaders: () => HeadersInit
  onError: (message: string | null) => void
}

const SamplesSection: React.FC<SamplesSectionProps> = ({ getSessionHeaders, onError }) => {
  const { t } = useTranslation()
  const [samplesRunning, setSamplesRunning] = useState<boolean>(false)
  const [samplesMessage, setSamplesMessage] = useState<string | null>(null)
  const [generatedFiles, setGeneratedFiles] = useState<SampleReportFile[]>([])

  const runSampleGeneration = async (): Promise<void> => {
    setSamplesRunning(true)
    setSamplesMessage(null)
    onError(null)
    try {
      const response = await fetch('/api/CalculationReport/samples/run', {
        method: 'POST',
        headers: getSessionHeaders()
      })

      if (!response.ok) {
        let apiError = t('admin.samples.errGenerate')
        try {
          const payload = await response.json()
          if (payload?.message) {
            apiError = payload.message
          }
        } catch {
          // ignore
        }
        throw new Error(apiError)
      }

      const payload = await response.json()
      const fileCount = Array.isArray(payload?.files) ? payload.files.length : 0
      const directory = payload?.outputDirectory ?? t('admin.samples.serverOutputDir')
      setGeneratedFiles(payload?.files || [])
      setSamplesMessage(t('admin.samples.generated', { count: fileCount, directory }))
    } catch (err) {
      onError(t('admin.samples.failed', { message: (err as Error).message }))
    } finally {
      setSamplesRunning(false)
    }
  }

  return (
    <>
      {samplesMessage && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {samplesMessage}
          <button type="button" className="btn-close" onClick={() => setSamplesMessage(null)}></button>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">
            <i className="bi bi-file-earmark-pdf me-2"></i>
            {t('admin.samples.title')}
          </h5>
        </div>
        <div className="card-body">
          <p className="text-muted">
            {t('admin.samples.intro')}
          </p>

          <button
            className="btn btn-primary"
            onClick={runSampleGeneration}
            disabled={samplesRunning}
          >
            {samplesRunning ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {t('admin.samples.generating')}
              </>
            ) : (
              <>
                <i className="bi bi-play-fill me-2"></i>
                {t('admin.samples.run')}
              </>
            )}
          </button>

          <div className="mt-4">
            <h6>{t('admin.samples.sampleTypes')}</h6>
            {generatedFiles.length > 0 ? (
              <ul className="list-group">
                {generatedFiles.map((file, index) => (
                  <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                    <div>
                      <i className="bi bi-file-earmark-pdf text-danger me-2"></i>
                      <strong className="text-capitalize">{file.materialType}</strong> {t('admin.samples.beamReportSuffix')}
                      <span className="text-muted ms-2">({(file.fileSizeBytes / 1024).toFixed(1)} KB)</span>
                    </div>
                    <a
                      href={`/api/CalculationReport/samples/${file.fileName}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-outline-primary"
                    >
                      <i className="bi bi-download me-1"></i>
                      {t('admin.samples.open')}
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <ul>
                <li>{t('admin.samples.steelBeamReport')}</li>
                <li>{t('admin.samples.concreteBeamReport')}</li>
                <li>{t('admin.samples.timberBeamReport')}</li>
              </ul>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default SamplesSection
